import { useEffect, useRef, useState } from 'react'
import Header from '../components/layout/Header'
import ChartPlaceholder from '../components/portfolio/ChartPlaceholder'
import FilterMenu, {
  FILTER_TRIGGER_CLASS,
  FilterIcon,
} from '../components/portfolio/FilterMenu'
import ChartTabScroller from '../components/portfolio/ChartTabScroller'
import PillTab from '../components/portfolio/PillTab'
import PortfolioBillboard from '../components/portfolio/PortfolioBillboard'
import PositionsPanel from '../components/portfolio/PositionsPanel'
import StatRow from '../components/portfolio/StatRow'
import { PORTFOLIO_SEO } from '../config/seo'
import { usePageCss } from '../hooks/usePageCss'
import { useWalletAuth } from '../context/WalletAuthProvider'
import {
  ACTIVITY_ROWS,
  BALANCE_ROWS,
  CHART_TABS,
  LEFT_TABS,
  PERFORMANCE_ROWS,
  PERPS_ROWS,
} from '../data/portfolio'
import { usePageMeta } from '../hooks/usePageMeta'
import './portfolio-mobile.css'

function openConnect() {
  window.dispatchEvent(new Event('lighter:open-connect'))
}

function StatSection({ title, rows, className = '' }) {
  return (
    <section className={`flex flex-col gap-1.5 ${className}`.trim()}>
      {title ? (
        <span className="portfolio-stat-heading font-mono label-meta text-muted">
          {title}
        </span>
      ) : null}
      <div className="-mx-4 divide-y-[0.5px] divide-line">
        {rows.map((row) => (
          <StatRow key={row.testId} {...row} />
        ))}
      </div>
    </section>
  )
}

export default function Portfolio() {
  usePageMeta(PORTFOLIO_SEO)
  usePageCss('portfolio-page-css', '/css/lighter_portfolio.css')
  const { isConnected } = useWalletAuth()
  const [leftTab, setLeftTab] = useState('Overview')
  const [chartTab, setChartTab] = useState('Total Equity')
  const [perfType, setPerfType] = useState('Total Equity')
  const [perfPeriod, setPerfPeriod] = useState('1M')
  const [chartType, setChartType] = useState('Total Equity')
  const [chartPeriod, setChartPeriod] = useState('1M')
  const [openFilter, setOpenFilter] = useState(null)
  const leftFilterRef = useRef(null)
  const chartFilterRef = useRef(null)

  useEffect(() => {
    const onPointer = (event) => {
      if (
        event.target.closest(
          '[data-testid="portfolio-performance-filter-menu"], [data-testid="portfolio-chart-filter-menu"], [aria-label="Filter By"]'
        )
      ) {
        return
      }
      setOpenFilter(null)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [])

  return (
    <div className="portfolio-page flex h-dvh w-full flex-col overflow-hidden bg-bg text-ink">
      <Header />
      <PortfolioBillboard />
      <div
        data-testid="sub-page-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
        data-fade="y"
        style={{ '--fade-t': '0px', '--fade-b': '24px' }}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10 max-mobile:px-4 max-mobile:py-6">
          <section
            className="mb-9 max-mobile:mb-6"
            style={{ opacity: 1, transform: 'none' }}
          >
            <div className="flex items-end justify-between gap-6 max-mobile:flex-col max-mobile:items-start max-mobile:gap-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <h1 className="m-0 text-hero leading-[1.02] font-medium tracking-display [text-box-edge:cap_alphabetic] [text-box-trim:trim-start]">
                    Portfolio
                  </h1>
                  <div className="flex items-center gap-1.5 empty:hidden" />
                </div>
              </div>
              {!isConnected ? (
                <div
                  className="shrink-0 max-mobile:w-full"
                  data-testid="portfolio-connect-wallet-wrap"
                >
                  <div
                    className="flex flex-wrap justify-end gap-2 max-mobile:justify-start"
                    data-testid="portfolio-connect-wallet-inner"
                  >
                    <button
                      type="button"
                      data-testid="portfolio-connect-wallet-button"
                      onClick={openConnect}
                      className="group inline-flex h-10 w-fit flex-none cursor-pointer items-center justify-center gap-1.5 truncate rounded-pill rounded-modal! px-3.5 text-md/4 font-medium transition-all bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:scale-[0.97] active:bg-ink/80 disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] max-mobile:h-(--control-h-md) max-mobile:min-w-(--control-h-md) max-mobile:flex-1 max-mobile:text-sm/4"
                    >
                      <span>Connect Wallet</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section
            className="portfolio-overview-chart-grid grid grid-cols-2 items-stretch gap-3 max-tablet:grid-cols-1"
            style={{ opacity: 1, transform: 'none' }}
          >
            <div className="relative min-h-0">
              <div className="overflow-hidden rounded-panel border-[0.5px] border-line bg-fill flex min-h-0 flex-col absolute inset-0 max-tablet:static">
                <div className="card-header flex min-h-[40px] w-full shrink-0 items-center justify-between gap-2 border-b-[0.5px] border-line p-2">
                  <div className="relative min-w-0">
                    <div
                      className="-mx-2 -my-2 no-scrollbar min-w-0 overflow-x-auto px-2 py-2"
                      style={{ '--fade-l': '0px', '--fade-r': '0px' }}
                    >
                      <div className="flex items-stretch gap-1">
                        {LEFT_TABS.map((label) => (
                          <PillTab
                            key={label}
                            label={label}
                            pressed={leftTab === label}
                            onClick={() => setLeftTab(label)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  {leftTab === 'Performance' ? (
                    <div className="relative" ref={leftFilterRef}>
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={openFilter === 'perf'}
                        data-state={openFilter === 'perf' ? 'open' : 'closed'}
                        aria-label="Filter By"
                        className={FILTER_TRIGGER_CLASS}
                        onClick={() =>
                          setOpenFilter((current) =>
                            current === 'perf' ? null : 'perf'
                          )
                        }
                      >
                        <div
                          data-testid="portfolio-performance-filters"
                          className="flex items-center gap-1"
                        >
                          <FilterIcon />
                        </div>
                      </button>
                      {openFilter === 'perf' ? (
                        <FilterMenu
                          testId="portfolio-performance-filter-menu"
                          anchorRef={leftFilterRef}
                          type={perfType}
                          period={perfPeriod}
                          onType={setPerfType}
                          onPeriod={setPerfPeriod}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div
                  className={
                    leftTab === 'Overview'
                      ? 'portfolio-overview-body flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-hidden p-2'
                      : 'flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-2'
                  }
                >
                  <div className="flex flex-col gap-3">
                    {leftTab === 'Overview' ? (
                      <>
                        <StatSection title="Balances" rows={BALANCE_ROWS} />
                        <StatSection
                          title="Perpetuals Overview"
                          rows={PERPS_ROWS}
                          className="portfolio-overview-perps"
                        />
                      </>
                    ) : (
                      <>
                        <StatSection
                          title="Performance"
                          rows={PERFORMANCE_ROWS}
                        />
                        <StatSection title="Activity" rows={ACTIVITY_ROWS} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-panel border-[0.5px] border-line bg-fill flex min-w-0 flex-col">
              <div className="card-header flex min-h-[40px] w-full shrink-0 items-center justify-between gap-2 border-b-[0.5px] border-line p-2">
                <ChartTabScroller>
                  {CHART_TABS.map((label) => (
                    <PillTab
                      key={label}
                      label={label}
                      pressed={chartTab === label}
                      onClick={() => setChartTab(label)}
                    />
                  ))}
                </ChartTabScroller>
                <div className="relative" ref={chartFilterRef}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={openFilter === 'chart'}
                    data-state={openFilter === 'chart' ? 'open' : 'closed'}
                    aria-label="Filter By"
                    className={FILTER_TRIGGER_CLASS}
                    onClick={() =>
                      setOpenFilter((current) =>
                        current === 'chart' ? null : 'chart'
                      )
                    }
                  >
                    <div
                      data-testid="portfolio-chart-filters"
                      className="flex items-center gap-1"
                    >
                      <FilterIcon />
                    </div>
                  </button>
                  {openFilter === 'chart' ? (
                    <FilterMenu
                      testId="portfolio-chart-filter-menu"
                      anchorRef={chartFilterRef}
                      type={chartType}
                      period={chartPeriod}
                      onType={setChartType}
                      onPeriod={setChartPeriod}
                    />
                  ) : null}
                </div>
              </div>
              <ChartPlaceholder connected={isConnected} onConnect={openConnect} />
            </div>
          </section>

          <PositionsPanel connected={isConnected} />
        </div>
      </div>
    </div>
  )
}
